import React, { useState, useEffect, useRef, useCallback } from "react";
import AttachmentModal from "./models/attchment";
import PollModal from "./models/poll";
import VoiceSendModal from "./models/voicemodel";
import Cookies from "js-cookie";
import toast from "react-hot-toast";
import MessagesArea from "./chatMessageArea";
import {
  ensureGroupSocket,
  getGroupSocket,
  trackGroupPendingUpload,
  patchGroupPendingUpload,
  clearGroupPendingUpload,
  mergeGroupMessagesWithPending,
  subscribeGroupUploadEvents,
} from "../../services/groupSocketManager";
import { shouldFailUploadsOnDisconnect } from "../../services/chatSocketManager";
import {
  mirrorUploadStarted,
  mirrorUploadProgress,
  mirrorUploadFinished,
} from "../../services/durableMediaUpload";
import {
  logMediaUpload,
  MEDIA_UPLOAD_TIMEOUT_MS,
  shouldLogMediaUpload,
  summarizeSocketPayload,
} from "../../utils/mediaUploadLogger";

const getPollIdFromMessage = (message) => {
  const poll = message?.poll || message?.original?.poll;
  return poll?.poll_id ?? poll?.id ?? null;
};

const getMessageIds = (message) => {
  const ids = [
    message?.id,
    message?.message_id,
    message?.original?.message_id,
    message?.original?.id,
  ]
    .filter((id) => id != null && id !== "")
    .map(String);
  return [...new Set(ids)];
};

const isPollUpdatedFlag = (value) =>
  value === 1 || value === "1" || value === true;

/** Replace poll payload in place — never append a duplicate poll message */
const applyPollUpdate = (messages, poll, messageId, extra = {}) => {
  if (!poll?.poll_id && !messageId) {
    return { next: messages, matched: false };
  }

  const pollId = poll?.poll_id ?? poll?.id ?? null;
  const mid = messageId || (pollId ? `poll_${pollId}` : null);

  const index = messages.findIndex((message) => {
    const ids = getMessageIds(message);
    if (mid && ids.some((id) => id === String(mid))) return true;
    if (pollId != null) {
      const existingPollId = getPollIdFromMessage(message);
      if (existingPollId != null && String(existingPollId) === String(pollId)) return true;
      if (ids.some((id) => id === `poll_${pollId}`)) return true;
    }
    return false;
  });

  if (index === -1) return { next: messages, matched: false };

  const existing = messages[index];
  // Only overwrite my_choices when this payload is for the current viewer
  const pollForMerge = { ...poll };
  if (!extra?.__applyMyChoices) {
    delete pollForMerge.my_choices;
  }
  const mergedPoll = { ...(existing.poll || {}), ...pollForMerge };
  if (!extra?.__applyMyChoices && existing.poll && "my_choices" in existing.poll) {
    mergedPoll.my_choices = existing.poll.my_choices;
  }

  const next = [...messages];
  next[index] = {
    ...existing,
    id: mid || existing.id,
    type: "poll",
    poll: mergedPoll,
    options: mergedPoll.options || existing.options || [],
    poll_updated: 1,
    original: {
      ...existing.original,
      message_id: mid || existing.original?.message_id || existing.id,
      message_type: "poll",
      poll_updated: 1,
      poll: mergedPoll,
      voter_user_id: extra?.voter_user_id,
      voter_choices: extra?.voter_choices,
    },
  };
  return { next, matched: true };
};

const normalizeSocketPollPayload = (data, myUserId) => {
  if (!data) return null;
  const poll = data.poll ? { ...data.poll } : null;
  if (!poll) return null;

  const isOwnVote =
    myUserId != null &&
    data.voter_user_id != null &&
    String(data.voter_user_id) === String(myUserId);

  // Other users' votes must not blink our checkbox — strip their my_choices
  if (data.voter_user_id != null && !isOwnVote) {
    delete poll.my_choices;
  } else if (isOwnVote) {
    poll.my_choices = Array.isArray(data.voter_choices)
      ? data.voter_choices
      : data.voter_choices != null
        ? [data.voter_choices]
        : poll.my_choices || [];
  }

  return {
    poll,
    messageId: data.message_id || (poll.poll_id ? `poll_${poll.poll_id}` : null),
    groupId: data.group_id || null,
    applyMyChoices: isOwnVote || (data.voter_user_id == null && Array.isArray(poll.my_choices)),
  };
};

// ---------- MAIN CHAT SCREEN CONTAINER ----------
export default function GroupChatScreen ({ groupId, onBackMobile, groupList, groupData, onMarkAsRead, onMessageReceived, onHeaderClick, allContacts = [], onShareMessage }) {
  const [showAttachModal, setShowAttachModal] = useState(false);
  const [showVoiceAttachModal, setShowVoiceAttachModal] = useState(false);
  const [showVoice, setShowVoice] = useState(false);
  const [voiceTime, setVoiceTime] = useState("0:13");
  const [showVoiceSendModal, setShowVoiceSendModal] = useState(false);
  const [showPollModal, setShowPollModal] = useState(false);
  const [showPollResultModal, setShowPollResultModal] = useState(false);
  const [selectedPoll, setSelectedPoll] = useState(null);
  const [pendingAttachType, setPendingAttachType] = useState(null);
  const [messages, setMessages] = useState([]);
  const [groupMeta, setGroupMeta] = useState(null);
  const [message, setMessage] = useState("");
  const [profile, setProfile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0); // For legacy UI bar, can be removed later
  const [isUploading, setIsUploading] = useState(false); // For legacy UI bar, can be removed later
  // Remove an optimistic uploading message by tempId
  const handleCancelUpload = useCallback((tempId, groupIdForUpload = groupIdRef.current) => {
    clearActiveGroupUpload(tempId);
    mirrorUploadFinished(tempId);
    if (groupIdForUpload) clearGroupPendingUpload(groupIdForUpload, tempId);
    setMessages((prev) => {
      const removed = prev.find((m) => m.tempId === tempId);
      if (removed?.fileUrl?.startsWith("blob:")) {
        URL.revokeObjectURL(removed.fileUrl);
      }
      return prev.filter((m) => m.tempId !== tempId);
    });
    setIsUploading(false);
    setUploadProgress(0);
  }, []);

  const activeUploadsRef = useRef(new Map());

  const resolveFileType = (file, fileTypeHint) => {
    if (fileTypeHint === "photo" || fileTypeHint === "camera") {
      if (file?.type?.startsWith("video")) return "video";
      const ext = file?.name?.split(".")?.pop()?.toLowerCase() || "";
      const videoExts = ["mp4", "webm", "ogg", "mov", "avi", "mkv"];
      if (videoExts.includes(ext)) return "video";
      return "image";
    }
    if (fileTypeHint === "video") return "video";
    if (fileTypeHint === "document") return "document";
    if (file?.type?.startsWith("image")) return "image";
    if (file?.type?.startsWith("audio")) return "audio";
    if (file?.type?.startsWith("video")) return "video";
    const ext = file?.name?.split(".")?.pop()?.toLowerCase() || "";
    const imageExts = ["jpg", "jpeg", "png", "gif", "webp", "bmp", "svg"];
    const audioExts = ["mp3", "wav", "ogg", "aac", "m4a", "flac"];
    const videoExts = ["mp4", "webm", "ogg", "mov", "avi", "mkv"];
    if (imageExts.includes(ext)) return "image";
    if (audioExts.includes(ext)) return "audio";
    if (videoExts.includes(ext)) return "video";
    return "document";
  };

  const clearActiveGroupUpload = (tempId) => {
    activeUploadsRef.current.delete(tempId);
  };

  const markOptimisticUploadFailed = (groupIdForUpload, tempId, errorMsg) => {
    clearActiveGroupUpload(tempId);
    mirrorUploadFinished(tempId, { failed: true, error: errorMsg || "Upload failed" });
    if (groupIdForUpload) {
      patchGroupPendingUpload(groupIdForUpload, tempId, {
        uploading: false,
        uploadFailed: true,
        uploadError: errorMsg || "Upload failed",
      }, { notify: true });
    }
    setMessages((prev) =>
      prev.map((m) =>
        m.id === tempId || m.tempId === tempId
          ? { ...m, uploading: false, uploadFailed: true, uploadError: errorMsg || "Upload failed" }
          : m
      )
    );
  };

  const registerActiveGroupUpload = (groupIdForUpload, tempId, file, optimisticMessage, fileType) => {
    clearActiveGroupUpload(tempId);
    const startedAt = Date.now();
    const timeoutId = setTimeout(() => {
      if (!activeUploadsRef.current.has(tempId)) return;
      const socket = getGroupSocket();
      if (shouldLogMediaUpload(fileType, file)) {
        logMediaUpload("group", "emit-timeout", file, {
          tempId,
          fileType,
          groupId: groupIdForUpload,
          timeoutMs: MEDIA_UPLOAD_TIMEOUT_MS,
          elapsedMs: Date.now() - startedAt,
          socketConnected: Boolean(socket?.connected),
          socketId: socket?.id ?? null,
        });
      }
      markOptimisticUploadFailed(
        groupIdForUpload,
        tempId,
        "Upload timed out — the server did not respond. Please try again."
      );
      toast.error("Upload timed out. Please try again.");
    }, MEDIA_UPLOAD_TIMEOUT_MS);
    activeUploadsRef.current.set(tempId, { file, groupId: groupIdForUpload, startedAt, fileType });
    if (shouldLogMediaUpload(fileType, file)) {
      logMediaUpload("group", "registered-timeout", file, {
        tempId,
        fileType,
        groupId: groupIdForUpload,
        timeoutMs: MEDIA_UPLOAD_TIMEOUT_MS,
        startedAt: new Date(startedAt).toISOString(),
      });
    }
    if (groupIdForUpload && optimisticMessage) {
      const { onCancelUpload, ...stored } = optimisticMessage;
      trackGroupPendingUpload(groupIdForUpload, tempId, stored, timeoutId, { notify: false });
    }
  };

  const failActiveGroupUploadsOnDisconnect = (reason) => {
    if (!shouldFailUploadsOnDisconnect(reason)) {
      return;
    }
    const entries = [...activeUploadsRef.current.entries()];
    if (!entries.length) return;
    entries.forEach(([tempId, { groupId: gid, file, fileType, startedAt }]) => {
      if (shouldLogMediaUpload(fileType, file)) {
        logMediaUpload("group", "socket-disconnect-during-upload", file, {
          tempId,
          fileType,
          groupId: gid,
          reason,
          elapsedMs: startedAt ? Date.now() - startedAt : null,
        });
      }
      markOptimisticUploadFailed(gid, tempId, "Upload failed — connection lost while sending the file.");
    });
    toast.error("Upload failed — connection lost while sending the file.");
  };

  const attachCancelHandler = useCallback((msg) => {
    if (!msg?.tempId || !msg.uploading) return msg;
    return {
      ...msg,
      onCancelUpload: () => handleCancelUpload(msg.tempId, groupIdRef.current),
    };
  }, [handleCancelUpload]);
  
  const socketRef = useRef(null);
  const fileInputRef = useRef(null);
  const groupIdRef = useRef(null);
  const markedReadRef = useRef({});

  const handleViewOnceMedia = useCallback((message) => {
    if (!message?.id || !groupIdRef.current) return;
    const shareType = message.shareType || message.original?.share_type || "";
    if (shareType !== "sharetoview") return;
    const messageId = message.id;

    const socket = socketRef.current;
    if (socket?.connected) {
      socket.emit("mark_image_viewed", {
        group_id: groupIdRef.current,
        message_id: messageId,
      });
    }

    setMessages((prev) =>
      prev.map((m) => {
        if (String(m.id) !== String(messageId)) return m;
        const nextViewed = Array.isArray(m.viewedBy) ? [...m.viewedBy] : [];
        if (profile?.user_id && !nextViewed.some((id) => String(id) === String(profile.user_id))) {
          nextViewed.push(profile.user_id);
        }
        return {
          ...m,
          viewedBy: nextViewed,
          original: { ...m.original, viewed_by: nextViewed },
        };
      })
    );
  }, [profile?.user_id]);

  const normalizedGroupData = groupData
    ? {
        group_id: groupData.group_id || groupId,
        group_name: groupData.group_name || groupData.name || "Group",
        group_image_url: groupData.group_image_url || groupData.profile_image || "",
      }
    : null;

  // Prefer parent group list data, then direct groupData, then fallback from history/socket payloads
  const currentGroupFromList = Array.isArray(groupList)
    ? groupList.find((g) => String(g.group_id) === String(groupId))
    : null;
  const currentGroup = currentGroupFromList || normalizedGroupData || groupMeta || {
    group_id: groupId,
    group_name: "Group",
    group_image_url: "",
  };

  // Utility: normalize filenames by removing common server hash suffixes
  const getBaseFileName = (filename) => {
    if (!filename) return '';
    let name = String(filename).split('/').pop() || String(filename);
    name = name
      .replace(/_[a-f0-9]{8,}\./gi, '.')
      .replace(/-[a-f0-9]{8,}\./gi, '.')
      .replace(/_[a-f0-9]{32,}/gi, '')
      .replace(/-[a-f0-9]{32,}/gi, '')
      .toLowerCase()
      .trim();
    return name.replace(/[^a-z0-9\.]+/g, '');
  };

  const isWithinGroupUploadWindow = (message, windowMs = 120000) => {
    if (message?.uploadStartedAt) {
      return Date.now() - message.uploadStartedAt < windowMs;
    }
    return String(message?.id || "").startsWith("temp_");
  };

  const isLocalGroupUploadMessage = (message) => {
    if (!message?.isUser) return false;
    if (message.uploading || message.tempId || message._clientUploadId) return true;
    if (String(message.id || "").startsWith("temp_")) return true;
    return isWithinGroupUploadWindow(message);
  };

  const resolveGroupServerFileUrl = (rawUrl) => {
    if (!rawUrl) return null;
    const value = String(rawUrl);
    if (value.startsWith("blob:")) return value;
    if (value.startsWith("http")) return value;
    return `${process.env.REACT_APP_API_BASE_URL}${value.startsWith("/") ? value : `/${value}`}`;
  };

  const getGroupMessageTimestamp = (message = {}) => {
    const raw =
      message?.created_at ||
      message?.timestamp ||
      message?.custom_time ||
      message?.time ||
      message?.original?.created_at ||
      message?.original?.timestamp ||
      message?.original?.custom_time ||
      message?.original?.time ||
      null;
    const time = raw ? new Date(raw).getTime() : 0;
    return Number.isFinite(time) ? time : 0;
  };
  
  // Update ref when groupId changes
  useEffect(() => {
    groupIdRef.current = groupId;
    // Reset the markedReadRef guard when switching groups
    if (groupId) {
      markedReadRef.current[String(groupId)] = false;
      setGroupMeta(null);
    }
  }, [groupId]);

  // ----------------------------
  // LOAD PROFILE
  // ----------------------------
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const token = Cookies.get("auth_token");
        if (!token) return;

        const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/profile`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const data = await res.json();
        setProfile(data?.data?.[0]?.profile || {});
      } catch (err) {
        }
    };
    fetchProfile();
  }, []);

  // ----------------------------
  // SOCKET CONNECTION & JOIN GROUP
  // ----------------------------
  useEffect(() => {
    if (!groupId) return;

    const token = Cookies.get("auth_token");
    if (!token) {
      return;
    }

    const socket = ensureGroupSocket(token);
    if (!socket) return;

    socketRef.current = socket;

    const joinCurrentGroup = () => {
      const gid = groupIdRef.current || groupId;
      if (!gid) return;
      socket.emit("join_group", { token, group_id: gid }, (ack) => {
        if (ack && ack.error) {
          toast.error(ack.error || "Failed to join group");
        }
      });
    };

    const onConnect = () => {
      joinCurrentGroup();
    };

    const onConnectError = (err) => {
      if (err.message && !err.message.includes("WebSocket") && !err.message.includes("/ws")) {
        toast.error("Failed to connect to group chat");
      }
    };

    const onJoined = (data) => {
      };

    const onJoinError = (e) => {
      };

    const onNewGroupMessage = (msg) => {
      // Keep header data available even when parent doesn't pass a group list
      if (msg?.group_name || msg?.name || msg?.group_image_url || msg?.group_image || msg?.profile_image) {
        setGroupMeta((prev) => ({
          group_id: groupIdRef.current || groupId,
          group_name: msg.group_name || msg.name || prev?.group_name || "Group",
          group_image_url: msg.group_image_url || msg.group_image || msg.profile_image || prev?.group_image_url || "",
        }));
      }

      // Live poll vote update: replace existing poll — do not append a new message
      if (
        isPollUpdatedFlag(msg?.poll_updated) &&
        msg?.message_type === "poll" &&
        msg?.poll
      ) {
        const normalized = normalizeSocketPollPayload(msg, profile?.user_id);
        if (normalized?.poll) {
          setMessages((prev) => {
            const result = applyPollUpdate(
              prev,
              normalized.poll,
              normalized.messageId || msg.message_id,
              { ...msg, __applyMyChoices: normalized.applyMyChoices }
            );
            return result.matched ? result.next : prev;
          });
        }
        return;
      }
      
      // Get current user phone from token
      let currentUserPhone = null;
      try {
        const token = Cookies.get("auth_token");
        if (token) {
          const base64 = token.split(".")[1];
          const payload = JSON.parse(atob(base64));
          currentUserPhone = payload.phone;
        }
      } catch (_) {}

      const isMe = currentUserPhone && msg.sender_phone === currentUserPhone;

      // Determine message type
      let messageType = "text";
      if (msg.message_type === "poll") messageType = "poll";
      else if (msg.message_type === "file" && msg.file_url) {
        if (msg.mime_type?.startsWith("image")) messageType = "image";
        else if (msg.mime_type?.startsWith("audio")) messageType = "audio";
        else if (msg.mime_type?.startsWith("video")) messageType = "video";
        else messageType = "document";
      }

      const shareType = msg.share_type || msg.shareType || "";
      const messageId = msg.message_id || msg.id || `group_${Date.now()}`;
      const viewedBy = Array.isArray(msg.viewed_by)
        ? msg.viewed_by
        : (Array.isArray(msg.viewedBy) ? msg.viewedBy : []);
      const fileUrl = msg.file_url || msg.file || null;
      const fileDeleted =
        Boolean(msg.file_deleted || msg.fileDeleted) ||
        (shareType === "sharetoview" && !fileUrl);

      const newMessage = {
        id: messageId,
        text: msg.message || msg.file_name || "",
        type: messageType,
        isUser: isMe || msg.is_user === 1 || msg.is_user === "1",
        created_at: msg.created_at || msg.timestamp || null,
        time: msg.custom_time || new Date().toLocaleTimeString(),
        senderName: msg.sender_name || "Unknown",
        senderAvatar: msg.sender_avatar || "",
        fileName: msg.file_name || null,
        fileUrl: fileUrl || null,
        mime: msg.mime_type || null,
        fileSize: msg.file_size_str || null,
        thumbnail_image: msg.thumbnail_image || msg.thumbnail_path || null,
        shareType,
        viewedBy,
        fileDeleted: fileDeleted || false,
        poll: msg.poll || null,
        options: msg.poll?.options || [],
        original: msg,
      };

      // New poll message: if same poll/message already exists, replace instead of duplicating
      if (messageType === "poll" && msg.poll) {
        let mergedExistingPoll = false;
        setMessages((prev) => {
          const result = applyPollUpdate(prev, msg.poll, messageId, msg);
          if (result.matched) {
            mergedExistingPoll = true;
            return result.next;
          }
          if (prev.some((m) => String(m.id) === String(newMessage.id))) {
            return prev;
          }
          return [...prev, newMessage];
        });
        if (mergedExistingPoll) {
          return;
        }
      }

      setMessages(prev => {
        if (prev.some((m) => String(m.id) === String(newMessage.id))) {
          return prev;
        }

        const matchesOptimistic = (m) => {
          try {
            if (!isMe || !isLocalGroupUploadMessage(m)) return false;
            if (!['image', 'document', 'video', 'audio'].includes(m.type)) return false;

            const serverName = String(newMessage.fileName || newMessage.original?.file_name || '').trim();
            const serverUrl = String(newMessage.fileUrl || newMessage.original?.file_url || '').trim().toLowerCase();

            if (m.fileName && serverName) {
              const a = getBaseFileName(m.fileName);
              const b = getBaseFileName(serverName);
              if (a && b && a === b) return true;
            }

            if (m._normalizedFileName && serverName) {
              const normServer = String(serverName || '').toLowerCase().replace(/[^a-z0-9]+/g, '');
              if (normServer && m._normalizedFileName === normServer) return true;
            }

            if (serverUrl && m.fileName) {
              const local = String(m.fileName).toLowerCase();
              if (local && serverUrl.includes(local)) return true;
            }

            const incomingSizeRaw = Number(newMessage.file_size || newMessage.original?.file_size || 0) || 0;
            if (m._rawFileSize && incomingSizeRaw && Math.abs(m._rawFileSize - incomingSizeRaw) <= 1024) return true;

            return false;
          } catch (e) {
            return false;
          }
        };

        const matchIndex = prev.findIndex((m) => matchesOptimistic(m));
            if (matchIndex !== -1) {
          const matched = prev[matchIndex];
          const uploadKey = matched.tempId || matched._clientUploadId || matched.id;

          if (matched?.fileUrl?.startsWith('blob:')) {
            try { URL.revokeObjectURL(matched.fileUrl); } catch (_) { /* ignore */ }
          }
          if (uploadKey && groupIdRef.current) {
            clearGroupPendingUpload(groupIdRef.current, uploadKey, { notify: false });
          }

          const merged = {
            ...newMessage,
            shareType: matched.shareType || newMessage.shareType,
            uploading: false,
            tempId: undefined,
            _clientUploadId: undefined,
          };

          return prev.flatMap((m) => {
            const sameUpload =
              m === matched ||
              (isMe &&
                uploadKey &&
                (m.tempId === uploadKey || m._clientUploadId === uploadKey || String(m.id) === String(uploadKey)));

            if (sameUpload) {
              if (m !== matched && m?.fileUrl?.startsWith('blob:')) {
                try { URL.revokeObjectURL(m.fileUrl); } catch (_) { /* ignore */ }
              }
              return m === matched ? [merged] : [];
            }

            const duplicateSameFile =
              isMe &&
              m.isUser &&
              newMessage.fileName &&
              m.fileName &&
              getBaseFileName(m.fileName) === getBaseFileName(newMessage.fileName) &&
              (
                isLocalGroupUploadMessage(m) ||
                String(m.id) === String(newMessage.id) ||
                (matched?.uploadStartedAt && getGroupMessageTimestamp(m) >= matched.uploadStartedAt - 15000)
              );

            if (duplicateSameFile) {
              if (m?.fileUrl?.startsWith('blob:')) {
                try { URL.revokeObjectURL(m.fileUrl); } catch (_) { /* ignore */ }
              }
              return [];
            }

            return [m];
          });
        }

        if (isMe && (newMessage.fileName || newMessage.fileUrl)) {
          const fallbackIdx = [...prev].reverse().findIndex(
            (m) =>
              m.isUser &&
              m.type !== "text" &&
              (isLocalGroupUploadMessage(m) || String(m.id || "").startsWith("temp_"))
          );
          if (fallbackIdx !== -1) {
            const idx = prev.length - 1 - fallbackIdx;
            const matched = prev[idx];
            const uploadKey = matched.tempId || matched._clientUploadId || matched.id;
            if (matched?.fileUrl?.startsWith("blob:")) {
              try { URL.revokeObjectURL(matched.fileUrl); } catch (_) { /* ignore */ }
            }
            if (uploadKey && groupIdRef.current) {
              clearGroupPendingUpload(groupIdRef.current, uploadKey, { notify: false });
            }
            const merged = {
              ...newMessage,
              shareType: matched.shareType || newMessage.shareType,
              uploading: false,
              tempId: undefined,
              _clientUploadId: undefined,
            };
            return prev.flatMap((m, i) => {
              if (i === idx) return [merged];
              const duplicateSameFile =
                m.isUser &&
                newMessage.fileName &&
                m.fileName &&
                getBaseFileName(m.fileName) === getBaseFileName(newMessage.fileName) &&
                (
                  isLocalGroupUploadMessage(m) ||
                  String(m.id) === String(newMessage.id) ||
                  (matched?.uploadStartedAt && getGroupMessageTimestamp(m) >= matched.uploadStartedAt - 15000)
                );
              if (duplicateSameFile) {
                if (m?.fileUrl?.startsWith("blob:")) {
                  try { URL.revokeObjectURL(m.fileUrl); } catch (_) { /* ignore */ }
                }
                return [];
              }
              return [m];
            });
          }

          const hasRecentSameFile = prev.some(
            (m) =>
              m.isUser &&
              m.fileName &&
              newMessage.fileName &&
              getBaseFileName(m.fileName) === getBaseFileName(newMessage.fileName) &&
              isLocalGroupUploadMessage(m)
          );
          if (hasRecentSameFile) {
            return prev;
          }

          if (newMessage.fileName || newMessage.fileUrl || (newMessage.type !== "text" && newMessage.type !== "audio")) {
            return prev;
          }
        }

        return [...prev, newMessage];
      });

      // Notify parent about new message ONLY if from others (for group list update with unread icon)
      if (!isMe && onMessageReceived && groupIdRef.current) {
        onMessageReceived(groupIdRef.current);
      }

      // If this group is open and message is from others, mark as read
      if (!isMe && groupIdRef.current) {
        const lastMessageId = newMessage.original?.message_id || newMessage.id;
        if (lastMessageId) {
          // Reset guard and call mark read for new incoming message
          const groupKey = String(groupIdRef.current);
          markedReadRef.current[groupKey] = false;
          setTimeout(() => {
            markMessagesAsRead(groupIdRef.current, lastMessageId);
          }, 100);
        }
      }
    };

    const onSendError = (e) => {
      toast.error(e?.message || "Failed to send message");
    };

    const onUploadError = (e) => {
      toast.error(e?.message || "Upload failed");
      setIsUploading(false);
      setUploadProgress(0);
    };

    const handlePollSocketUpdate = (data) => {
      const normalized = normalizeSocketPollPayload(data, profile?.user_id);
      if (!normalized?.poll) return;

      let matched = false;
      setMessages((prev) => {
        const result = applyPollUpdate(
          prev,
          normalized.poll,
          normalized.messageId,
          { ...data, __applyMyChoices: normalized.applyMyChoices }
        );
        matched = result.matched;
        return result.matched ? result.next : prev;
      });

      // Fallback if poll isn't in the open chat yet
      if (!matched && typeof window.loadChatHistory === "function") {
        window.loadChatHistory();
      }
    };

    const onPollVoteUpdated = (data) => {
      handlePollSocketUpdate(data);
    };

    const onGroupPollUpdated = (data) => {
      handlePollSocketUpdate(data);
    };

    const onImageViewed = (payload) => {
      const data = payload?.data || payload;
      const messageId = data?.message_id;
      if (!messageId) return;
      setMessages((prev) =>
        prev.map((m) => {
          if (String(m.id) !== String(messageId)) return m;
          const nextViewed = Array.isArray(m.viewedBy) ? [...m.viewedBy] : [];
          if (profile?.user_id && !nextViewed.some((id) => String(id) === String(profile.user_id))) {
            nextViewed.push(profile.user_id);
          }
          return {
            ...m,
            viewedBy: nextViewed,
            fileDeleted: data?.file_deleted || m.fileDeleted,
            original: {
              ...m.original,
              viewed_by: nextViewed,
              file_deleted: data?.file_deleted || m.fileDeleted,
            },
          };
        })
      );
    };

    const onDisconnect = (reason) => {
      failActiveGroupUploadsOnDisconnect(reason);
    };

    if (socket.connected) joinCurrentGroup();
    socket.on("connect", onConnect);
    socket.on("connect_error", onConnectError);
    socket.on("joined", onJoined);
    socket.on("join_error", onJoinError);
    socket.on("new_group_message", onNewGroupMessage);
    socket.on("send_error", onSendError);
    socket.on("upload_error", onUploadError);
    // Backend live poll vote events (primary + aliases)
    socket.on("poll_vote_updated", onPollVoteUpdated);
    socket.on("group_poll_updated", onGroupPollUpdated);
    socket.on("poll_vote_update", onPollVoteUpdated);
    socket.on("poll_updated", onGroupPollUpdated);
    socket.on("image_viewed", onImageViewed);
    socket.on("disconnect", onDisconnect);

    return () => {
      socket.off("connect", onConnect);
      socket.off("connect_error", onConnectError);
      socket.off("joined", onJoined);
      socket.off("join_error", onJoinError);
      socket.off("new_group_message", onNewGroupMessage);
      socket.off("send_error", onSendError);
      socket.off("upload_error", onUploadError);
      socket.off("poll_vote_updated", onPollVoteUpdated);
      socket.off("group_poll_updated", onGroupPollUpdated);
      socket.off("poll_vote_update", onPollVoteUpdated);
      socket.off("poll_updated", onGroupPollUpdated);
      socket.off("image_viewed", onImageViewed);
      socket.off("disconnect", onDisconnect);
    };
  }, [groupId, profile?.user_id]);
  // ----------------------------
  // MARK MESSAGES AS READ
  // ----------------------------
  const markMessagesAsRead = async (group_id, up_to_message_id) => {
    if (!group_id || !up_to_message_id) return;
    
    const key = String(group_id);
    // Guard to prevent duplicate API calls
    if (markedReadRef.current[key] === true || markedReadRef.current[key] === 'pending') {
      return;
    }
    
    markedReadRef.current[key] = 'pending';
    
    try {
      const token = Cookies.get("auth_token");
      if (!token) return;

      const params = new URLSearchParams();
      params.append("group_id", group_id);
      params.append("up_to_message_id", up_to_message_id);
      const res = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/group/messages/mark_read`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/x-www-form-urlencoded"
          },
          body: params.toString()
        }
      );

      const data = await res.json();

      markedReadRef.current[key] = true;
      
      // Update the group's read status in the parent component
      if (onMarkAsRead && group_id) {
        onMarkAsRead(group_id);
      }
    } catch (err) {
      markedReadRef.current[key] = false;
      // Don't show error toast for this, as it's not critical
    }
  };

  // ----------------------------
  // LOAD CHAT HISTORY (standalone function)
  // ----------------------------
  const loadChatHistory = async () => {
    if (!groupId) return;
    try {
      const token = Cookies.get("auth_token");
      if (!token) return;
      const params = new URLSearchParams();
      params.append("group_id", groupId);
      params.append("limit", "100");
      params.append("offset", 0);
      const res = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/group/messages`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/x-www-form-urlencoded"
          },
          body: params.toString()
        }
      );
      const data = await res.json();

      // Capture group metadata from API payload for header fallback
      const historyGroup = Array.isArray(data?.data) && data.data.length > 0 ? data.data[0] : null;
      if (historyGroup && (historyGroup.group_name || historyGroup.name || historyGroup.group_image_url || historyGroup.group_image || historyGroup.profile_image)) {
        setGroupMeta((prev) => ({
          group_id: historyGroup.group_id || groupId,
          group_name: historyGroup.group_name || historyGroup.name || prev?.group_name || "Group",
          group_image_url: historyGroup.group_image_url || historyGroup.group_image || historyGroup.profile_image || prev?.group_image_url || "",
        }));
      }
      // Get current user phone
      let currentUserPhone = null;
      try {
        if (token) {
          const base64 = token.split(".")[1];
          const payload = JSON.parse(atob(base64));
          currentUserPhone = payload.phone;
        }
      } catch (_) {}
      let messagesArr = [];
      // Handle nested data structure: data.data[0].messages (array of group objects)
      if (Array.isArray(data?.data) && data.data.length > 0 && Array.isArray(data.data[0].messages)) {
        messagesArr = data.data[0].messages;
      } else if (data?.data?.messages && Array.isArray(data.data.messages)) {
        messagesArr = data.data.messages;
      } else if (Array.isArray(data?.data)) {
        messagesArr = data.data;
      } else if (Array.isArray(data?.messages)) {
        messagesArr = data.messages;
      }
      const formatted = messagesArr.map((m) => {
        let messageType = "text";
        if (m.message_type === "poll") messageType = "poll";
        else if (m.message_type === "file" && m.file_url) {
          if (m.mime_type?.startsWith("image")) messageType = "image";
          else if (m.mime_type?.startsWith("audio")) messageType = "audio";
          else if (m.mime_type?.startsWith("video")) messageType = "video";
          else messageType = "document";
        }
        const isMe = (currentUserPhone && m.sender_phone === currentUserPhone) || m.is_user === 1 || m.is_user === "1";
        const messageId = m.message_id || m.id || `msg_${Date.now()}`;
        const shareType = m.share_type || m.shareType || "";
        const fileUrl = m.file_url || m.file || null;
        const viewedBy = Array.isArray(m.viewed_by)
          ? m.viewed_by
          : (Array.isArray(m.viewedBy) ? m.viewedBy : []);
        const fileDeleted =
          Boolean(m.file_deleted || m.fileDeleted) ||
          (shareType === "sharetoview" && !fileUrl);
        return {
          id: messageId,
          text: m.message || m.file_name || "",
          type: messageType,
          isUser: isMe,
          created_at: m.created_at || m.timestamp || null,
          time: m.custom_time || new Date().toLocaleTimeString(),
          senderName: m.sender_name || "Unknown",
          senderAvatar: m.senderAvatar || "",
          fileName: m.file_name || null,
          fileUrl: fileUrl || null,
          mime: m.mime_type || null,
          fileSize: m.file_size_str || null,
          thumbnail_image: m.thumbnail_image || m.thumbnail_path || null,
          shareType,
          viewedBy,
          fileDeleted: fileDeleted || false,
          poll: m.poll || null,
          options: m.poll?.options || [],
          original: m,
        };
      });
      setMessages(mergeGroupMessagesWithPending(formatted, groupId).map(attachCancelHandler));
      
      // Mark messages as read when chat opens (after loading chat history)
      // This is called when user clicks to open a chat
      if (formatted.length > 0) {
        // Get the last message (most recent) to mark all messages up to it as read
        const lastMessage = formatted[formatted.length - 1];
        const lastMessageId = lastMessage.original?.message_id || lastMessage.id;
        if (lastMessageId && groupId) {
          markMessagesAsRead(groupId, lastMessageId);
        }
      }
    } catch (err) {
      toast.error("Failed to load chat history");
    }
  };

  // Expose for legacy usage (window.loadChatHistory)
  useEffect(() => {
    window.loadChatHistory = loadChatHistory;
    return () => {
      if (window.loadChatHistory === loadChatHistory) {
        window.loadChatHistory = undefined;
      }
    };
  }, [groupId]);
  // Call on mount and when groupId changes
  useEffect(() => {
    loadChatHistory();
  }, [groupId]);

  useEffect(() => {
    return subscribeGroupUploadEvents((event, { groupId: gid, tempId, message }) => {
      if (String(gid) !== String(groupIdRef.current)) return;

      if (event === "removed") {
        setMessages((prev) => prev.filter((m) => m.tempId !== tempId));
        return;
      }

      if (event === "added") {
        setMessages((prev) => {
          if (prev.some((m) => m.tempId === tempId)) return prev;
          return [...prev, attachCancelHandler(message)];
        });
        return;
      }

      setMessages((prev) => {
        const idx = prev.findIndex((m) => m.tempId === tempId);
        const withHandlers = attachCancelHandler(message);
        if (idx === -1) return prev;
        return prev.map((m) => (m.tempId === tempId ? withHandlers : m));
      });
    });
  }, [attachCancelHandler]);

  // ----------------------------
  // SEND MESSAGE
  // ----------------------------
  const handleSendMessage = () => {
    if (!message || !message.trim()) {
      return;
    }

    if (!groupId) {
      toast.error("No group selected");
      return;
    }

    if (!socketRef.current || !socketRef.current.connected) {
      toast.error("Not connected to group chat. Please wait...");
      return;
    }

    const token = Cookies.get("auth_token");
    if (!token) {
      toast.error("Not authenticated");
      return;
    }

    const messageText = message.trim();
    // Clear input immediately
    setMessage("");

    socketRef.current.emit("send_group_message", {
      token,
      group_id: groupId,
      message: messageText,
    }, (ack) => {
      if (ack && ack.error) {
        toast.error(ack.error || "Failed to send message");
      } else {
        // The server will send new_group_message event which will add the message
      }
    });
  };

  // ----------------------------
  // SEND FILE
  // ----------------------------
  const handleFileInput = async (file, fileTypeHint, shareTypeOverride = "") => {
    if (!file || !groupId) return;
    let previewUrl = null;
    const groupIdForUpload = groupId;
    const chatSocket = getGroupSocket();
    const fileType = resolveFileType(file, fileTypeHint);
    const token = Cookies.get("auth_token");
    const logUpload = (phase, extra = {}) => {
      if (!shouldLogMediaUpload(fileType, file)) return;
      logMediaUpload("group", phase, file, {
        fileType,
        fileTypeHint,
        groupId: groupIdForUpload,
        shareTypeOverride,
        socketConnected: Boolean(chatSocket?.connected),
        socketId: chatSocket?.id ?? null,
        tokenPresent: Boolean(token),
        apiBaseUrl: process.env.REACT_APP_API_BASE_URL ?? null,
        ...extra,
      });
    };

    logUpload("start");

    if (!chatSocket || !chatSocket.connected) {
      toast.error("Not connected to group chat");
      logUpload("aborted-not-connected");
      return;
    }

    if (!token) {
      logUpload("aborted-no-token");
      return;
    }

    try {
      setIsUploading(true);
      setUploadProgress(0);
      const tempId = `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      const formatFileSize = (bytes) => {
        if (bytes < 1024) return `${bytes} B`;
        if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
        return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
      };
      const shareTypeToSend =
        (fileType === "image" || fileType === "video") && shareTypeOverride ? shareTypeOverride : "";
      previewUrl = fileType === "image" || fileType === "video" ? URL.createObjectURL(file) : null;

      const optimisticMessage = {
        id: tempId,
        tempId,
        _clientUploadId: tempId,
        text: file.name,
        type: fileType,
        isUser: true,
        time: new Date().toISOString(),
        fileName: file.name,
        fileUrl: previewUrl,
        mime: file.type || undefined,
        fileSize: formatFileSize(file.size),
        _rawFileSize: file.size,
        _normalizedFileName: String(file.name || "").toLowerCase().replace(/[^a-z0-9]+/g, ""),
        uploading: true,
        uploadProgress: 0,
        uploadStartedAt: Date.now(),
        shareType: shareTypeToSend,
      };

      setMessages((prev) => [
        ...prev,
        {
          ...optimisticMessage,
          onCancelUpload: () => handleCancelUpload(tempId, groupIdForUpload),
        },
      ]);
      registerActiveGroupUpload(groupIdForUpload, tempId, file, optimisticMessage, fileType);
      mirrorUploadStarted({
        tempId,
        kind: "group",
        targetId: groupIdForUpload,
        file,
        fileType,
        shareType: shareTypeToSend,
        previewUrl,
      });
      logUpload("reading-file", { tempId, uploadStartedAt: optimisticMessage.uploadStartedAt });

      const reader = new FileReader();
      let lastLoggedReadProgress = -1;
      let lastMirroredProgress = -1;
      reader.onprogress = (e) => {
        if (e.lengthComputable) {
          const pct = Math.round((e.loaded / e.total) * 100);
          // Avoid remounting message bubbles on every FileReader tick (loader flicker).
          if (pct - lastMirroredProgress >= 10 || pct >= 100) {
            lastMirroredProgress = pct;
            mirrorUploadProgress(tempId, pct, "reading");
            patchGroupPendingUpload(groupIdForUpload, tempId, { uploadProgress: pct }, { notify: false });
          }
          const milestone = Math.floor(pct / 25) * 25;
          if (milestone > lastLoggedReadProgress && milestone > 0) {
            lastLoggedReadProgress = milestone;
            logUpload("read-progress", {
              tempId,
              percent: milestone,
              loadedBytes: e.loaded,
              totalBytes: e.total,
            });
          }
        }
      };
      reader.onerror = () => {
        setIsUploading(false);
        setUploadProgress(0);
        toast.error("Failed to read file");
        logUpload("read-error", {
          tempId,
          error: reader.error?.message || String(reader.error),
        });
        markOptimisticUploadFailed(groupIdForUpload, tempId, "Failed to read file");
      };
      reader.onload = () => {
        try {
          const bytes = new Uint8Array(reader.result);
          const emitStartedAt = Date.now();
          mirrorUploadProgress(tempId, 100, "uploading");
          patchGroupPendingUpload(groupIdForUpload, tempId, { uploadProgress: 100 }, { notify: false });

          const payload = {
            token,
            group_id: groupIdForUpload,
            filename: file.name,
            mime_type: file.type || undefined,
            file: bytes,
            share_type: shareTypeToSend,
          };
          logUpload("emit-send_group_file", {
            tempId,
            event: "send_group_file",
            byteLength: bytes.length,
            shareTypeToSend,
            emitStartedAt: new Date(emitStartedAt).toISOString(),
            payload: summarizeSocketPayload(payload),
          });

          chatSocket.emit(
            "send_group_file",
            payload,
            (ack) => {
              const ackElapsedMs = Date.now() - emitStartedAt;
              clearActiveGroupUpload(tempId);
              setIsUploading(false);
              setUploadProgress(0);

              logUpload("ack-received", {
                tempId,
                ackElapsedMs,
                ackIsUndefined: ack === undefined,
                ack,
              });

              if (ack && ack.error) {
                if (previewUrl) URL.revokeObjectURL(previewUrl);
                logUpload("ack-error", { tempId, ackElapsedMs, error: ack.error, ack });
                markOptimisticUploadFailed(groupIdForUpload, tempId, ack.error || "Upload failed");
                toast.error(ack.error || "Upload failed");
                return;
              }

              mirrorUploadFinished(tempId);

              const serverMessageId = ack?.message_id || ack?.id || ack?.data?.message_id || null;
              const returnedFile = ack && (ack.file_url || ack.fileUrl || ack.url || ack.path || ack.file_path);
              const returnedFileName = ack && (ack.filename || ack.fileName || ack.name);
              const returnedMime = ack && (ack.mime_type || ack.mime || ack.content_type);
              const returnedThumbnail = ack && (ack.thumbnail_image || ack.thumbnail_path || null);

              if (returnedFile) {
                const resolved =
                  typeof returnedFile === "string"
                    ? returnedFile
                    : returnedFile.url || returnedFile.path || returnedFile.file_url || "";
                const resolvedUrl = resolveGroupServerFileUrl(resolved);
                const patch = {
                  uploading: false,
                  uploadFailed: false,
                  uploadError: undefined,
                  fileUrl: resolvedUrl,
                  fileName: returnedFileName || file.name,
                  mime: returnedMime || file.type || undefined,
                  thumbnail_image: returnedThumbnail,
                  tempId: undefined,
                  _clientUploadId: tempId,
                  type: fileType,
                  ...(serverMessageId ? { id: serverMessageId } : {}),
                };
                patchGroupPendingUpload(groupIdForUpload, tempId, patch, { notify: false });
                clearGroupPendingUpload(groupIdForUpload, tempId, { notify: false });
                setMessages((prev) =>
                  prev.map((m) => {
                    if (m.id !== tempId && m.tempId !== tempId && m._clientUploadId !== tempId) {
                      return m;
                    }
                    if (m.fileUrl?.startsWith("blob:") && resolvedUrl) {
                      try { URL.revokeObjectURL(m.fileUrl); } catch (_) { /* ignore */ }
                    }
                    return { ...m, ...patch, shareType: m.shareType || shareTypeToSend };
                  })
                );
                logUpload("ack-success-with-url", {
                  tempId,
                  ackElapsedMs,
                  resolvedUrl,
                  serverMessageId,
                });
              } else {
                logUpload("ack-success-no-url", {
                  tempId,
                  ackElapsedMs,
                  serverMessageId,
                  note: "Waiting for new_group_message event to finalize",
                });
                patchGroupPendingUpload(groupIdForUpload, tempId, {
                  uploading: false,
                  _clientUploadId: tempId,
                  ...(serverMessageId ? { id: serverMessageId } : {}),
                }, { notify: false });
                setMessages((prev) =>
                  prev.map((m) =>
                    (m.id === tempId || m.tempId === tempId || m._clientUploadId === tempId)
                      ? {
                          ...m,
                          uploading: false,
                          _clientUploadId: tempId,
                          ...(serverMessageId ? { id: serverMessageId } : {}),
                        }
                      : m
                  )
                );
              }
            }
          );
        } catch (err) {
          setIsUploading(false);
          setUploadProgress(0);
          if (previewUrl) URL.revokeObjectURL(previewUrl);
          logUpload("emit-error", { tempId, error: err?.message || String(err) });
          markOptimisticUploadFailed(groupIdForUpload, tempId, err.message || "Upload failed");
          toast.error("Upload failed");
        }
      };
      logUpload("read-as-array-buffer-start", { tempId });
      reader.readAsArrayBuffer(file);
    } catch (err) {
      setIsUploading(false);
      setUploadProgress(0);
      if (typeof previewUrl !== "undefined" && previewUrl) URL.revokeObjectURL(previewUrl);
      toast.error("Upload failed");
      logUpload("unexpected-error", { error: err?.message || String(err) });
    }
  };

  const handleFileSelect = (fileOrFiles, fileTypeHint, shareTypeOverride = "") => {
    if (!fileOrFiles) return;
    const files = Array.isArray(fileOrFiles)
      ? fileOrFiles
      : Array.from(fileOrFiles instanceof FileList ? fileOrFiles : [fileOrFiles]);

    files.forEach((file) => {
      if (!file) return;
      handleFileInput(file, fileTypeHint, shareTypeOverride);
    });
  };

  function handleAttachPick (type) {
    setShowAttachModal(false);
    setShowVoiceAttachModal(false);

    if (type === "poll") {
      setShowPollModal(true);
    } else {
      // Handle file selection
      const input = fileInputRef.current;
      if (!input) return;

      setPendingAttachType(type);

      if (type === "photo") {
        input.accept = "image/*,video/*";
        input.removeAttribute("capture");
        input.multiple = true;
      } else if (type === "camera") {
        input.accept = "image/*";
        input.setAttribute("capture", "environment");
        input.multiple = false;
      } else if (type === "document") {
        input.accept = "*/*";
        input.removeAttribute("capture");
        input.multiple = true;
      }

      input.value = "";
      setTimeout(() => input.click(), 10);
    }
  }

  const handleApplyPollUpdate = useCallback((poll, messageId) => {
    if (!poll) return;
    setMessages((prev) => {
      const result = applyPollUpdate(
        prev,
        poll,
        messageId || (poll.poll_id ? `poll_${poll.poll_id}` : null),
        { __applyMyChoices: true }
      );
      return result.matched ? result.next : prev;
    });
  }, []);

  const handlePollSubmit = async (pollData) => {
    if (!groupId) {
      toast.error("No group selected");
      return;
    }
    const socket = socketRef.current;
    if (!socket || !socket.connected) {
      toast.error("Socket not connected");
      return;
    }

    try {
      // Prepare poll data for socket emission
      const pollPayload = {
        group_id: groupId,
        question: pollData.question,
        options: Array.isArray(pollData.options) ? pollData.options : pollData.options.split(","),
        allow_multiple: pollData.allowMultiple ? 1 : 0,
      };

      // Add timer only if provided
      if (pollData.timer && pollData.timer !== "0") {
        pollPayload.timer = pollData.timer;
      }

      // Emit new_polls socket event with acknowledgment callback
      socket.emit('new_polls', pollPayload, (response) => {
        if (response && response.error) {
          toast.error(response.error || "Failed to create poll");
        } else if (response && response.success) {
          toast.success("Poll created successfully!");
        } else {
          }
      });
      setShowPollModal(false);

      // Optionally refresh history to show the newly created poll
      // The socket listener should handle adding it, but this ensures it's there
      setTimeout(() => {
        loadChatHistory();
      }, 500);

    } catch (err) {
      toast.error("Failed to create poll");
    }
  };

  function handleMicButton () {
    setShowVoice(true);
    setShowAttachModal(false);
    setShowVoiceAttachModal(false);
  }

  function handleReturnToInput () {
    setShowVoice(false);
    setShowVoiceAttachModal(false);
  }

  function handleSendVoiceClick (action) {
    if (action === "send" || action === undefined) {
      setShowVoiceSendModal(true);
    }
    if (action === "delete") setShowVoice(false);
  }

  function handleVoiceSendModalDelete () {
    setShowVoice(false);
    setShowVoiceSendModal(false);
    setVoiceTime("0:00");
  }

  function handleVoiceSendModalSend () {
    setShowVoice(false);
    setShowVoiceSendModal(false);
  }

  function handleVoiceAttachmentClick () {
    setShowVoiceAttachModal(!showVoiceAttachModal);
  }


  return (
    <div className="relative w-full h-full">
      <VoiceSendModal
        open={showVoiceSendModal}
        groupId={groupId}
        onClose={() => {
          setShowVoiceSendModal(false);
          setShowVoice(false);
        }}
      />

      <PollModal
        open={showPollModal}
        onClose={() => setShowPollModal(false)}
        onSubmit={handlePollSubmit}
      />

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        style={{ display: "none" }}
        onChange={(e) => {
          const files = Array.from(e.target.files || []);
          if (files.length > 0) {
            handleFileSelect(files, pendingAttachType || undefined);
          }
          e.target.value = "";
        }}
      />

      <MessagesArea
        messages={messages}
        contact={currentGroup}
        message={message}
        setMessage={setMessage}
        onSendMessage={handleSendMessage}
        uploadProgress={uploadProgress}
        isUploading={isUploading}
        onBackMobile={onBackMobile}
        showVoice={showVoice}
        voiceTime={voiceTime}
        onMicButton={handleMicButton}
        onSendVoiceClick={handleSendVoiceClick}
        onReturnToInput={handleReturnToInput}
        showAttachModal={showAttachModal}
        setShowAttachModal={setShowAttachModal}
        handleAttachPick={handleAttachPick}
        onVoiceAttachmentClick={handleVoiceAttachmentClick}
        showVoiceAttachModal={showVoiceAttachModal}
        setShowVoiceAttachModal={setShowVoiceAttachModal}
        showPollModal={showPollModal}
        setShowPollModal={setShowPollModal}
        showPollResultModal={showPollResultModal}
        setShowPollResultModal={setShowPollResultModal}
        selectedPoll={selectedPoll}
        setSelectedPoll={setSelectedPoll}
        handleFileInput={handleFileSelect}
        groupId={groupId}
        onHeaderClick={onHeaderClick}
        currentUserId={profile?.user_id}
        allContacts={allContacts}
        onShareMessage={onShareMessage}
        onViewOnceMedia={handleViewOnceMedia}
        onPollUpdated={handleApplyPollUpdate}
      />
    </div>
  );
}